import { Link } from 'react-router-dom';
import { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import img1 from "../assets/hero/slider-1.webp";
import img2 from "../assets/hero/slide-2.webp";
import img3 from "../assets/hero/slide-3.webp";
import img6 from "../assets/hero/slide-4.webp";

function Hero({
  title,
  subtitle,
  description,
  ctaText = 'Get Started',
  ctaLink = '/enquiry',
  showCTA = true,
  backgroundPattern = true,
}) {
  const swiperRef = useRef(null);
 
  // Carousel slides data
  const slides = [
    {
      image: img1,
      title: title || "Environmental Solutions for a Sustainable Future",
      description: description || "Comprehensive testing, consultancy, and waste management services backed by ISO/NABL accreditation and multidisciplinary expertise.",
      subtitle: subtitle || "Leading Environmental Services"
    },
    {
      image: img2,
      title: "Expert Environmental Testing & Analysis",
      description: "State-of-the-art laboratory facilities with NABL-accredited testing procedures for accurate and reliable results.",
      subtitle: "ISO 17025:2017 Accredited"
    },
    {
      image: img3,
      title: "Comprehensive Environmental Consultancy",
      description: "Expert guidance on environmental compliance, waste management, and sustainable practices for your business.",
      subtitle: "Expert Consultancy Services"
    },
    {
      image: img6,
      title: "Trusted by Industry Leaders",
      description: "Delivering environmental excellence with rapid turnaround times and comprehensive reporting for businesses across sectors.",
      subtitle: "Your Trusted Partner"
    }
  ];

  return (
    <section className="relative h-[80vh] max-h-[85vh] overflow-hidden">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        effect="slide"
        spaceBetween={0}
        slidesPerView={1}
        loop={true}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        speed={800}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
        pagination={{
          clickable: true,
          bulletClass: 'swiper-pagination-bullet-minimal',
          bulletActiveClass: 'swiper-pagination-bullet-active-minimal',
        }}
        className="h-full w-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="relative h-full w-full">
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.image})` }}
                aria-hidden="true"
              ></div>
              
              {/* Overlay - Gradient for better readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

              {backgroundPattern && (
                <div
                  className="absolute inset-0 bg-grid-pattern opacity-0"
                  aria-hidden="true"
                ></div>
              )}

              {/* Content */}
              <div className="container mx-auto h-full flex items-center px-4 sm:px-6 lg:px-12 relative z-10 py-12 md:py-16">
                <div className="max-w-4xl mx-auto text-center">
                  {slide.subtitle && (
                    <div className="inline-block mb-3 px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs md:text-sm font-medium animate-fade-in border border-white/30">
                      {slide.subtitle}
                    </div>
                  )}

                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-5 tracking-tight animate-fade-in drop-shadow-lg">
                    {slide.title}
                  </h1>

                  {slide.description && (
                    <p className="text-base md:text-lg text-white/90 mb-6 md:mb-8 max-w-3xl mx-auto animate-fade-in animation-delay-200 drop-shadow-md leading-relaxed">
                      {slide.description}
                    </p>
                  )}

                  {showCTA && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center animate-fade-in animation-delay-400">
                      <Link to={ctaLink} className="btn-primary shadow-xl">
                        {ctaText}
                      </Link>
                      <Link to="/contact" className="btn-outline bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20">
                        Contact Us
                      </Link>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Minimal Navigation Arrows */}
      <button
        onClick={() => swiperRef.current?.slidePrev()}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label="Previous slide"
      >
        <svg
          className="w-4 h-4 md:w-5 md:h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        onClick={() => swiperRef.current?.slideNext()}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label="Next slide"
      >
        <svg
          className="w-4 h-4 md:w-5 md:h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

    </section>
  );
}

export default Hero;
